/** biome-ignore lint/a11y/noLabelWithoutControl: Labels are associated via nesting or htmlFor but biome doesn't always detect it correctly */
/** biome-ignore-all lint/a11y/noLabelWithoutControl: <explanation> */
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, ChevronDown, Loader2, Plus, Star } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useDebounce } from "usehooks-ts";
import { z } from "zod";
import AdminPageHeader from "@/components/AdminPageHeader";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	PointGrantSource,
	type RewardsGrantUser,
	usePointGrants,
	useRewardsUsers,
} from "@/hooks/usePointGrants";
import { usePointGrantsActivity } from "@/hooks/usePointGrantsActivity";
import { cn } from "@/lib/utils";
import IsConnectedWrapper from "../components/IsConnectedWrapper";

const inputClassName =
	"w-full bg-bg-secondary border border-divider rounded-lg px-3 py-2.5 text-text-default placeholder-text-placeholder text-sm focus:outline-none focus:ring-1 focus:ring-brand/50 focus:border-brand/50 transition-all";

const labelClassName = "text-sm font-medium text-text-secondary mb-1.5 block";

const POINT_GRANT_SOURCE_OPTIONS = [
	{ value: "trivia", label: "Trivias" },
	{ value: "missions", label: "Missions" },
	{ value: "ap_1.0", label: "AP 1.0" },
	{ value: "other", label: "Other" },
] as const;

const pointGrantFormSchema = z.object({
	userId: z.string().uuid("Select a user from search results"),
	points: z.number().int().positive("Points must be greater than 0"),
	source: z.enum(["trivia", "missions", "ap_1.0", "other"], {
		message: "Source is required",
	}),
	sourceRef: z.string().trim().min(1, "Source reference is required"),
	description: z.string().optional(),
	metadataJson: z.string().optional(),
});

type PointGrantFormData = z.infer<typeof pointGrantFormSchema>;

const createPointGrantDefaults = (): PointGrantFormData => ({
	userId: "",
	points: 50,
	source: "trivia",
	sourceRef: "",
	description: "",
	metadataJson: "",
});

const formatActivityDate = (value: string) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "Unknown date";
	return date.toLocaleString();
};

export default function GrantPointsPage() {
	const [pointGrantDialogOpen, setPointGrantDialogOpen] = useState(false);
	const [metadataOpen, setMetadataOpen] = useState(false);
	const [pointGrantUserSearch, setPointGrantUserSearch] = useState("");
	const [selectedRewardsUser, setSelectedRewardsUser] =
		useState<RewardsGrantUser | null>(null);
	const debouncedPointGrantUserSearch = useDebounce(pointGrantUserSearch, 300);

	const pointGrantForm = useForm<PointGrantFormData>({
		resolver: zodResolver(pointGrantFormSchema),
		defaultValues: createPointGrantDefaults(),
	});

	const pointGrantsMutation = usePointGrants();
	const isGrantSubmitting = pointGrantsMutation.isPending;
	const sourceOptions = POINT_GRANT_SOURCE_OPTIONS;

	const {
		data: rewardsUsersData,
		isLoading: rewardsUsersLoading,
		error: rewardsUsersError,
	} = useRewardsUsers({
		query: debouncedPointGrantUserSearch,
		limit: 8,
		enabled: pointGrantDialogOpen,
	});
	const rewardsUsers = rewardsUsersData?.users ?? [];

	const [page, setPage] = useState(1);
	const PAGE_SIZE = 10;

	const {
		data: pointsActivityData,
		isLoading: pointsActivityLoading,
		error: pointsActivityError,
	} = usePointGrantsActivity({
		limit: PAGE_SIZE,
		offset: (page - 1) * PAGE_SIZE,
	});
	const pointsActivityEntries = pointsActivityData?.entries ?? [];
	const totalGrants = pointsActivityData?.total ?? 0;
	const totalPages = Math.ceil(totalGrants / PAGE_SIZE);

	const formatRewardsUserLabel = useCallback((user: RewardsGrantUser) => {
		const display =
			user.displayName ||
			user.email ||
			user.selectedShinkaiIdentity ||
			user.walletAddress ||
			user.id;
		const secondary =
			user.email ||
			user.selectedShinkaiIdentity ||
			user.walletAddress ||
			user.discordUsername;
		return secondary && secondary !== display
			? `${display} · ${secondary}`
			: display;
	}, []);

	const openPointGrantDialog = useCallback(() => {
		pointGrantForm.reset(createPointGrantDefaults());
		setPointGrantUserSearch("");
		setSelectedRewardsUser(null);
		setMetadataOpen(false);
		pointGrantsMutation.reset();
		setPointGrantDialogOpen(true);
	}, [pointGrantForm, pointGrantsMutation]);

	const closePointGrantDialog = useCallback(() => {
		setPointGrantDialogOpen(false);
		pointGrantForm.reset(createPointGrantDefaults());
		setPointGrantUserSearch("");
		setSelectedRewardsUser(null);
		setMetadataOpen(false);
		pointGrantsMutation.reset();
	}, [pointGrantForm, pointGrantsMutation]);

	const handleSelectRewardsUser = useCallback(
		(user: RewardsGrantUser) => {
			pointGrantForm.setValue("userId", user.id, {
				shouldDirty: true,
				shouldValidate: true,
			});
			setPointGrantUserSearch(formatRewardsUserLabel(user));
			setSelectedRewardsUser(user);
			pointGrantForm.clearErrors("userId");

			const currentSourceRef = pointGrantForm.getValues("sourceRef").trim();
			if (!currentSourceRef) {
				const source = pointGrantForm.getValues("source") || "other";
				const date = new Date().toISOString().slice(0, 10);
				pointGrantForm.setValue(
					"sourceRef",
					`${source}-${date}-${user.id.slice(0, 8)}`,
					{
						shouldDirty: true,
					},
				);
			}
		},
		[formatRewardsUserLabel, pointGrantForm],
	);

	const clearSelectedRewardsUser = useCallback(() => {
		setSelectedRewardsUser(null);
		setPointGrantUserSearch("");
		pointGrantForm.setValue("userId", "", {
			shouldDirty: true,
			shouldValidate: false,
		});
		pointGrantForm.setValue("sourceRef", "", {
			shouldDirty: true,
		});
		pointGrantForm.clearErrors("userId");
	}, [pointGrantForm]);

	const onPointGrantSubmit = async (data: PointGrantFormData) => {
		if (!selectedRewardsUser) {
			pointGrantForm.setError("userId", {
				type: "manual",
				message: "Select a user from search results",
			});
			return;
		}

		const selectedSourceOption = sourceOptions.find(
			(sourceOption) => sourceOption.value === data.source.trim(),
		);
		if (!selectedSourceOption) {
			pointGrantForm.setError("source", {
				type: "manual",
				message: "Select a valid source",
			});
			return;
		}

		let metadata: Record<string, unknown> | null = null;
		const rawMetadata = data.metadataJson?.trim();

		if (rawMetadata) {
			try {
				const parsed = JSON.parse(rawMetadata) as unknown;
				if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
					pointGrantForm.setError("metadataJson", {
						type: "manual",
						message: "Metadata must be a valid JSON object",
					});
					return;
				}
				metadata = parsed as Record<string, unknown>;
			} catch {
				pointGrantForm.setError("metadataJson", {
					type: "manual",
					message: "Metadata must be valid JSON",
				});
				return;
			}
		}

		try {
			await pointGrantsMutation.mutateAsync({
				grants: [
					{
						userId: selectedRewardsUser.id,
						points: data.points,
						source: selectedSourceOption.value as PointGrantSource,
						sourceRef: data.sourceRef.trim(),
						description: data.description?.trim() || null,
						metadata,
					},
				],
			});

			toast.success("Manual points granted");
			closePointGrantDialog();
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to grant points",
			);
		}
	};

	const timelineItems = useMemo(() => {
		return pointsActivityEntries.map((entry) => ({
			...entry,
			title: entry.description?.trim() || entry.source || "Point grant",
			recipientLabel:
				entry.recipient?.displayName ||
				entry.recipient?.email ||
				entry.recipient?.selectedShinkaiIdentity ||
				entry.recipient?.walletAddress ||
				entry.userId,
		}));
	}, [pointsActivityEntries]);

	return (
		<main className="grow pb-20 px-6 font-inter min-h-[calc(100vh-500px)]">
			<div className="max-w-5xl mx-auto space-y-8">
				<IsConnectedWrapper>
					<AdminPageHeader
						title="Grant Points"
						description="Review grants you submitted and create new grants."
						actions={
							<Button
								type="button"
								onClick={openPointGrantDialog}
								className="gap-2"
							>
								<Plus className="w-4 h-4" />
								Submit New Grant
							</Button>
						}
					/>

					<section className="space-y-4">
						<div className="flex items-center justify-between px-1">
							<div className="space-y-1">
								<p className="text-xs text-text-tertiary">
									Showing {pointsActivityEntries.length} of {totalGrants} total
									grants
								</p>
							</div>
							{totalPages > 1 && (
								<div className="flex items-center gap-2">
									<Button
										variant="outline"
										size="xs"
										onClick={() => setPage((p) => Math.max(1, p - 1))}
										disabled={page === 1 || pointsActivityLoading}
										className="h-7 w-7 p-0 border-divider"
									>
										<span className="sr-only">Previous</span>
										&lt;
									</Button>
									<span className="text-xs text-text-tertiary font-mono">
										{page} / {totalPages}
									</span>
									<Button
										variant="outline"
										size="xs"
										onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
										disabled={page === totalPages || pointsActivityLoading}
										className="h-7 w-7 p-0 border-divider"
									>
										<span className="sr-only">Next</span>
										&gt;
									</Button>
								</div>
							)}
						</div>

						{pointsActivityLoading && (
							<div className="rounded-xl border border-dashed border-divider p-12 flex items-center justify-center gap-2 text-sm text-text-tertiary bg-bg-secondary/20">
								<Loader2 className="w-5 h-5 animate-spin text-brand" />
								<span>Loading activity...</span>
							</div>
						)}

						{pointsActivityError instanceof Error && (
							<div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 flex items-center gap-3">
								<AlertCircle className="w-5 h-5 text-destructive shrink-0" />
								<div>
									<h3 className="text-sm font-medium text-destructive">
										Error loading activity
									</h3>
									<p className="text-xs text-destructive/80 mt-1">
										{pointsActivityError.message}
									</p>
								</div>
							</div>
						)}

						{!pointsActivityLoading &&
							!(pointsActivityError instanceof Error) &&
							timelineItems.length === 0 && (
								<div className="rounded-xl border border-dashed border-divider p-12 text-center bg-bg-secondary/20">
									<div className="w-12 h-12 rounded-full bg-bg-tertiary/50 mx-auto flex items-center justify-center mb-3">
										<Star className="w-5 h-5 text-text-tertiary" />
									</div>
									<h3 className="text-sm font-medium text-text-secondary">
										No grants found
									</h3>
									<p className="text-xs text-text-tertiary mt-1 max-w-xs mx-auto">
										You haven't submitted any point grants yet. Click "Submit
										New Grant" to get started.
									</p>
								</div>
							)}

						{!pointsActivityLoading &&
							!(pointsActivityError instanceof Error) &&
							timelineItems.length > 0 && (
								<div className="rounded-xl border border-divider overflow-hidden bg-bg-secondary/30">
									<div className="overflow-x-auto">
										<table className="w-full text-left text-sm">
											<thead className="bg-bg-tertiary/50 text-text-tertiary font-medium border-b border-divider">
												<tr>
													<th className="px-5 py-3.5 whitespace-nowrap font-medium text-xs uppercase tracking-wider">
														Recipient
													</th>
													<th className="px-5 py-3.5 whitespace-nowrap font-medium text-xs uppercase tracking-wider">
														Points
													</th>
													<th className="px-5 py-3.5 whitespace-nowrap font-medium text-xs uppercase tracking-wider">
														Reason / Description
													</th>
													<th className="px-5 py-3.5 whitespace-nowrap font-medium text-xs uppercase tracking-wider">
														Source / Ref
													</th>
													<th className="px-5 py-3.5 text-right whitespace-nowrap font-medium text-xs uppercase tracking-wider">
														Date
													</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-divider/50">
												{timelineItems.map((entry) => (
													<tr
														key={entry.id}
														className="hover:bg-bg-tertiary/30 transition-colors group"
													>
														<td className="px-5 py-4 align-top">
															<div className="flex flex-col">
																<span
																	className="font-medium text-text-default text-sm truncate max-w-[200px]"
																	title={entry.recipientLabel}
																>
																	{entry.recipientLabel}
																</span>
																<span className="text-[10px] text-text-tertiary font-mono truncate max-w-[150px]">
																	{entry.userId}
																</span>
															</div>
														</td>
														<td className="px-5 py-4 align-top">
															<span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-brand/10 text-brand font-semibold text-xs border border-brand/20 shadow-sm shadow-brand/5">
																+{entry.points.toLocaleString()}
															</span>
														</td>
														<td className="px-5 py-4 align-top">
															<span
																className="block text-text-secondary text-sm max-w-[280px] truncate"
																title={entry.title}
															>
																{entry.title}
															</span>
															{entry.description &&
																entry.description !== entry.title && (
																	<span className="block text-xs text-text-tertiary mt-0.5 truncate max-w-[280px]">
																		{entry.description}
																	</span>
																)}
														</td>
														<td className="px-5 py-4 align-top">
															<div className="flex flex-col gap-1">
																{entry.source && (
																	<span className="inline-flex items-center px-1.5 py-0.5 rounded bg-bg-tertiary border border-divider text-[10px] text-text-secondary w-fit capitalize">
																		{entry.source}
																	</span>
																)}
																{entry.sourceRef && (
																	<code
																		className="text-[10px] text-text-tertiary font-mono truncate max-w-[140px] bg-bg-tertiary/30 px-1 rounded select-all"
																		title={entry.sourceRef}
																	>
																		{entry.sourceRef}
																	</code>
																)}
															</div>
														</td>
														<td className="px-5 py-4 text-right text-text-tertiary text-xs whitespace-nowrap align-top">
															{formatActivityDate(entry.grantedAt)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}
					</section>

					<Dialog
						open={pointGrantDialogOpen}
						onOpenChange={(open) => {
							if (open) {
								setPointGrantDialogOpen(true);
								return;
							}
							closePointGrantDialog();
						}}
					>
						<DialogContent className="max-w-xl">
							<form
								onSubmit={pointGrantForm.handleSubmit(onPointGrantSubmit)}
								className="space-y-4"
							>
								<DialogHeader>
									<DialogTitle>Grant Manual Points</DialogTitle>
									<DialogDescription>
										Award points for one user at a time.
									</DialogDescription>
								</DialogHeader>

								<div className="space-y-3">
									<span className={labelClassName}>
										Search and Select User *
									</span>
									<div className="flex items-center gap-2">
										<Input
											type="text"
											value={pointGrantUserSearch}
											onChange={(event) => {
												setPointGrantUserSearch(event.target.value);
												pointGrantForm.clearErrors("userId");
											}}
											disabled={Boolean(selectedRewardsUser)}
											placeholder="Search by email, identity, wallet, Discord..."
										/>
										{selectedRewardsUser && (
											<Button
												type="button"
												variant="outline"
												onClick={clearSelectedRewardsUser}
												className="border-divider shrink-0"
											>
												Change
											</Button>
										)}
									</div>
									<p className="text-[10px] text-text-tertiary">
										{selectedRewardsUser
											? "User selected. Click Change if you need a different user."
											: "Type at least 2 characters, then select one user from the results."}
									</p>

									{!selectedRewardsUser &&
										pointGrantUserSearch.trim().length >= 2 && (
											<div className="rounded-lg border border-divider bg-bg-tertiary/20 max-h-60 overflow-y-auto mt-2 shadow-lg">
												{rewardsUsersLoading ? (
													<div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-text-tertiary">
														<Loader2 className="w-4 h-4 animate-spin text-brand" />
														<span>Searching users...</span>
													</div>
												) : rewardsUsersError instanceof Error ? (
													<div className="px-4 py-3 text-sm text-destructive bg-destructive/5 m-2 rounded">
														{rewardsUsersError.message}
													</div>
												) : rewardsUsers.length === 0 ? (
													<div className="px-4 py-6 text-center text-sm text-text-tertiary">
														No users found matching "{pointGrantUserSearch}"
													</div>
												) : (
													<div className="p-1 space-y-0.5">
														{rewardsUsers.map((user) => (
															<button
																key={user.id}
																type="button"
																onClick={() => handleSelectRewardsUser(user)}
																className="w-full text-left px-3 py-2.5 rounded-md hover:bg-brand/10 hover:text-brand-foreground transition-all group flex items-center justify-between gap-3"
															>
																<div className="min-w-0 flex-1">
																	<p className="text-sm font-medium text-text-default group-hover:text-brand truncate">
																		{formatRewardsUserLabel(user)}
																	</p>
																	<p className="text-[10px] text-text-tertiary font-mono truncate mt-0.5">
																		{user.id}
																	</p>
																</div>
																<Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 text-brand transition-opacity" />
															</button>
														))}
													</div>
												)}
											</div>
										)}

									{pointGrantForm.formState.errors.userId &&
										!selectedRewardsUser && (
											<p className="text-xs text-destructive">
												{pointGrantForm.formState.errors.userId.message}
											</p>
										)}
								</div>

								<div
									className={cn(
										"rounded-lg border px-3 py-2",
										selectedRewardsUser
											? "border-brand/30 bg-brand/5"
											: "border-divider bg-bg-tertiary/20",
									)}
								>
									<p className="text-[10px] text-text-tertiary uppercase tracking-wider mb-1">
										Selected User
									</p>
									{selectedRewardsUser ? (
										<>
											<p className="text-xs text-text-default">
												{formatRewardsUserLabel(selectedRewardsUser)}
											</p>
											<p className="text-[10px] text-text-tertiary">
												{selectedRewardsUser.id}
											</p>
										</>
									) : (
										<p className="text-xs text-text-tertiary">
											No user selected yet.
										</p>
									)}
								</div>

								<div className="grid grid-cols-2 gap-3">
									<label className="block">
										<span className={labelClassName}>Points *</span>
										<Input
											type="number"
											min={1}
											{...pointGrantForm.register("points", {
												valueAsNumber: true,
											})}
											className={cn(
												pointGrantForm.formState.errors.points &&
													"border-destructive focus-visible:ring-destructive/50",
											)}
										/>
										{pointGrantForm.formState.errors.points && (
											<p className="text-xs text-destructive mt-1">
												{pointGrantForm.formState.errors.points.message}
											</p>
										)}
									</label>

									<div className="block">
										<span className={labelClassName}>Source *</span>
										<Controller
											control={pointGrantForm.control}
											name="source"
											render={({ field }) => (
												<Select
													value={field.value}
													onValueChange={field.onChange}
												>
													<SelectTrigger
														className={cn(
															"w-full",
															pointGrantForm.formState.errors.source &&
																"border-destructive focus-visible:ring-destructive/50",
														)}
													>
														<SelectValue placeholder="Select source" />
													</SelectTrigger>
													<SelectContent>
														{sourceOptions.map((sourceOption) => (
															<SelectItem
																key={sourceOption.value}
																value={sourceOption.value}
															>
																{sourceOption.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											)}
										/>
										{pointGrantForm.formState.errors.source && (
											<p className="text-xs text-destructive mt-1">
												{pointGrantForm.formState.errors.source.message}
											</p>
										)}
									</div>
								</div>

								<label className="block">
									<span className={labelClassName}>Source Reference *</span>
									<Input
										type="text"
										{...pointGrantForm.register("sourceRef")}
										placeholder="trivia-2026-02-16-user123"
										className={cn(
											pointGrantForm.formState.errors.sourceRef &&
												"border-destructive focus-visible:ring-destructive/50",
										)}
									/>
									<p className="text-[10px] text-text-tertiary mt-1">
										Use a stable value so retries do not double-award points.
									</p>
									{pointGrantForm.formState.errors.sourceRef && (
										<p className="text-xs text-destructive mt-1">
											{pointGrantForm.formState.errors.sourceRef.message}
										</p>
									)}
								</label>

								<label className="block">
									<span className={labelClassName}>Description</span>
									<Input
										type="text"
										{...pointGrantForm.register("description")}
										placeholder="Trivia winner"
									/>
								</label>

								<Collapsible open={metadataOpen} onOpenChange={setMetadataOpen}>
									<div className="rounded-lg border border-divider bg-bg-tertiary/20 p-3">
										<CollapsibleTrigger asChild>
											<Button
												type="button"
												variant="tertiary"
												size="xs"
												className="gap-2"
											>
												<ChevronDown
													className={cn(
														"h-3.5 w-3.5 transition-transform",
														metadataOpen && "rotate-180",
													)}
												/>
												Optional Metadata
											</Button>
										</CollapsibleTrigger>
										<CollapsibleContent className="mt-3">
											<textarea
												rows={4}
												{...pointGrantForm.register("metadataJson")}
												placeholder='{"gameId":"abc","note":"optional"}'
												className={cn(
													inputClassName,
													"font-mono text-xs",
													pointGrantForm.formState.errors.metadataJson &&
														"border-destructive focus:border-destructive focus:ring-destructive/50",
												)}
											/>
											<p className="text-[10px] text-text-tertiary mt-1">
												Optional JSON object. Leave empty if not needed.
											</p>
											{pointGrantForm.formState.errors.metadataJson && (
												<p className="text-xs text-destructive mt-1">
													{pointGrantForm.formState.errors.metadataJson.message}
												</p>
											)}
										</CollapsibleContent>
									</div>
								</Collapsible>

								{pointGrantsMutation.error instanceof Error && (
									<div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3">
										<p className="text-xs text-destructive">
											{pointGrantsMutation.error.message}
										</p>
									</div>
								)}

								<div className="flex justify-end gap-2 pt-2">
									<Button
										type="button"
										variant="outline"
										onClick={closePointGrantDialog}
										disabled={isGrantSubmitting}
										className="border-divider"
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={isGrantSubmitting || !selectedRewardsUser}
										className="gap-2"
									>
										{isGrantSubmitting ? (
											<Loader2 className="w-4 h-4 animate-spin" />
										) : (
											<Star className="w-4 h-4" />
										)}
										{isGrantSubmitting ? "Granting..." : "Grant Points"}
									</Button>
								</div>
							</form>
						</DialogContent>
					</Dialog>
				</IsConnectedWrapper>
			</div>
		</main>
	);
}
